import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetAllDocumentsRequestListAdmin,
  useGetListForSponsorFilters,
  useGetprovidersListForFilters,
  UseGetRfiListForFilters,
} from "../../custom-hooks/queries";
import SkeletonLoader from "../../components/SkeletonLoader";
import Pagination from "../../components/CustomPagination";
import Notification from "../../components/Notification";
import PageToolbarFilters from "../../components/Filters/PageToolbarFilters";
import ToolbarSearch from "../../components/Filters/ToolbarSearch";
import { generateV2ServiceCategoryOptions } from "../../utils/serviceCategoryV2Mapping";
import TableExportMenu from "../../components/Menus/TableExportMenu";
import { documentsCellStyles } from "../../utils/exportBadgeStyles";
import { setSearchTextByPage } from "../../redux-store/features/providerFiltersSlice";
import {
  getLibraryRequestsExportHeaders,
  getLibraryRequestsTableRows,
  LIBRARY_REQUESTS_USE_SERVER_SORT,
  mapLibraryRequestItemToExportRecord,
  sortConfigToLibraryRequestsApiParams,
} from "../../utils/libraryRequestsGridMapper";
import LibraryRequestsGridTable from "../../components/LibraryRequests/LibraryRequestsGridTable";

const BACKEND_LIBRARY_REQUESTS_PAGE_KEY = "adminLibraryRequests";

/** Default document status filter: all except Archived. */
const LIBRARY_ADMIN_DEFAULT_STATUS = ["Released", "Pending", "Cancelled"];

export const BackendUserAssessment = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({});
  const [sortFetchNonce, setSortFetchNonce] = useState(0);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    status: "error",
  });

  const { data: providers } = useGetprovidersListForFilters();
  const { data: sponsors } = useGetListForSponsorFilters();
  const { data: rfis } = UseGetRfiListForFilters();

  const v2ServiceOptions = useMemo(
    () => generateV2ServiceCategoryOptions(rfis || []),
    [rfis]
  );

  const dispatch = useDispatch();

  const searchQuery =
    useSelector(
      (state) =>
        state.providersFilters.searchTextByPage[BACKEND_LIBRARY_REQUESTS_PAGE_KEY]
    ) || "";

  const [selectedIds, setSelectedIds] = useState({
    sponsor: [],
    provider: [],
    docType: [],
    services: [],
    status: [...LIBRARY_ADMIN_DEFAULT_STATUS],
  });

  const filters = useMemo(
    () => [
      {
        id: "sponsor",
        name: "Sponsor",
        searchable: true,
        options: sponsors || [],
        total: sponsors?.length || 0,
      },
      {
        id: "provider",
        name: "Vendor",
        searchable: true,
        options: providers || [],
        total: 1335,
      },
      {
        id: "docType",
        name: "Document Type",
        searchable: false,
        options: [
          { docId: "RFI", label: "VQQ", checked: false },
          { docId: "VQA", label: "Audit Report", checked: false },
        ],
        total: 2,
      },
      {
        id: "services",
        name: "Scope",
        searchable: true,
        isV2: true,
        options: v2ServiceOptions,
        total: v2ServiceOptions?.length || 0,
      },
      {
        id: "status",
        name: "Status",
        searchable: false,
        options: [
          { statusID: "Released", label: "Released", checked: false },
          { statusID: "Pending", label: "Pending", checked: false },
          { statusID: "Cancelled", label: "Cancelled", checked: false },
          { statusID: "Archived", label: "Archived", checked: false },
        ],
        total: 4,
      },
    ],
    [sponsors, providers, v2ServiceOptions]
  );

  const serviceNames = useMemo(() => {
    return (
      selectedIds.services?.map((serviceId) => {
        const service = rfis?.find(
          (rfi) =>
            rfi.rfi_id === serviceId ||
            rfi.id === serviceId ||
            rfi.hash_id === serviceId
        );
        return (
          service?.services ||
          service?.rfi_name ||
          service?.name ||
          serviceId
        );
      }) || []
    );
  }, [selectedIds.services, rfis]);

  const rfiIds = [];

  const serverSortParams = useMemo(() => {
    if (!LIBRARY_REQUESTS_USE_SERVER_SORT) return undefined;
    const { orderBy, sortDirection } =
      sortConfigToLibraryRequestsApiParams(sortConfig);
    if (!orderBy || !sortDirection) return undefined;
    return { orderBy, sortDirection };
  }, [sortConfig]);

  const {
    data: requestList,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetAllDocumentsRequestListAdmin(
    currentPage,
    selectedIds.docType,
    selectedIds.provider,
    selectedIds.status,
    rfiIds,
    serviceNames,
    selectedIds.sponsor,
    searchQuery,
    serverSortParams,
    sortFetchNonce
  );

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      const newDirection =
        prevConfig[key]?.direction === "asc" ? "desc" : "asc";
      return { [key]: { direction: newDirection } };
    });
    if (LIBRARY_REQUESTS_USE_SERVER_SORT) {
      setSortFetchNonce((n) => n + 1);
      setCurrentPage(1);
    }
  };

  const sortedItems = useMemo(
    () =>
      getLibraryRequestsTableRows(
        requestList,
        sortConfig,
        LIBRARY_REQUESTS_USE_SERVER_SORT,
        "admin"
      ),
    [requestList, sortConfig]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedIds, searchQuery]);

  useEffect(() => {
    if (isError && error) {
      setNotification({
        show: true,
        message: error?.message || "An error occurred while loading data",
        status: "error",
      });
    }
  }, [isError, error]);

  const exportDataRef = useRef(null);

  useEffect(() => {
    exportDataRef.current = () => {
      const records = sortedItems.map((item) =>
        mapLibraryRequestItemToExportRecord(item, { audience: "admin" })
      );
      return {
        records,
        headers: getLibraryRequestsExportHeaders("admin"),
      };
    };
  }, [sortedItems]);

  return (
    <div className="w-full min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8 pb-6">
      <div className="shadow-header -mx-4 sm:-mx-6 lg:-mx-8 mb-6">
        <div className="bg-white py-4 flex flex-col gap-4 px-4 sm:px-6 lg:px-8 sm:flex-row sm:items-start sm:justify-between">
          <div className="sm:flex-auto min-w-0">
            <h1 className="text-lg font-medium text-gray-900">
              Library Requests
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              The table below displays all requested Vendor Qualification Questionnaires (VQQ)
              and Audit Reports from auditor-led qualification assessments across
              sponsors and vendors in the Diligent document library.
            </p>
          </div>
          <div className="flex shrink-0 justify-end mt-4">
            <TableExportMenu
              getData={() =>
                exportDataRef.current
                  ? exportDataRef.current()
                  : { records: [], headers: [] }
              }
              reportTitle="Library Requests"
              reportSubtitle="Documents requested across sponsors and vendors (admin)"
              filePrefix="Library_Requests_Admin"
              downloadType="Library Requests"
              cellStyles={documentsCellStyles}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 w-full min-w-0">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between w-full min-w-0">
          <div className="flex w-full min-w-0 flex-1 flex-col gap-3 lg:max-w-xl">
            <ToolbarSearch
              value={searchQuery}
              onDebouncedChange={(text) =>
                dispatch(
                  setSearchTextByPage({
                    pageKey: BACKEND_LIBRARY_REQUESTS_PAGE_KEY,
                    searchText: text,
                  })
                )
              }
              placeholder="Search documents..."
            />
          </div>
          <div className="w-full min-w-0 lg:w-auto lg:flex-shrink-0">
            <PageToolbarFilters
              sections={filters}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              serviceCategoryV2SourceData={rfis || []}
            />
          </div>
        </div>
      </div>

      <div className="w-full min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="px-4 py-6 sm:px-6">
            <SkeletonLoader />
          </div>
        ) : (
          <LibraryRequestsGridTable
            data={sortedItems}
            sortConfig={sortConfig}
            onSort={handleSort}
            refetch={refetch}
            isSearchActive={!!searchQuery}
            audience="admin"
            rowKey="request_id"
          />
        )}

        {!isError && !isLoading && sortedItems && sortedItems.length > 0 && (
          <div className="border-t border-gray-200">
            <Pagination
              totalPages={requestList?.total_pages || 1}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>

      <Notification
        show={notification.show}
        setShow={(show) => setNotification((prev) => ({ ...prev, show }))}
        errorMessage={notification.message}
        status={notification.status}
      />
    </div>
  );
};
